import { RequestRatingEnum } from "@nlc/shared-types";
import { TrendingUpIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { SearchField, SearchInput } from "@/components/search-field";
import {
  type SegmentedTabItem,
  SegmentedTabs,
} from "@/components/segmented-tabs";
import { useGetDesignerPerformanceSummary } from "@/features/profile/hooks/useGetDesignerPerformanceSummary";
import { DonutChart } from "@/features/projects/project/view/components/DonutChart";
import { RatingCard } from "@/features/projects/project/view/components/RatingCard";
import { useGetProjectView } from "@/features/projects/project/view/hooks/useGetProjectView";
import { percentages } from "@/features/projects/project/view/utils/percentagens";
import { usePaginationWithDynamicPageSize } from "@/hooks/usePaginationWithDynamicPageSize";
import { getPageSizeOptions } from "@/utils/getPageSizeOptions";

import { PerformanceRequestsTable } from "./PerformanceRequestsTable";

const DESIGNER_RATING_LABELS: Record<RequestRatingEnum, string> = {
  [RequestRatingEnum.GOOD]: "More than 4 stars",
  [RequestRatingEnum.NEUTRAL]: "3 to 4 stars",
  [RequestRatingEnum.POOR]: "Less than 3 stars",
};

type TimePeriod = "1mo" | "3mo" | "6mo" | "all" | "this-year";

const TIME_PERIOD_TABS: SegmentedTabItem<TimePeriod>[] = [
  { id: "all", label: "All" },
  { id: "1mo", label: "1 mo" },
  { id: "3mo", label: "3 mo" },
  { id: "6mo", label: "6 mo" },
  { id: "this-year", label: "This year" },
];

type DateRange = {
  completedAtFrom?: string;
  completedAtTo?: string;
};

function getDateRange(period: TimePeriod): DateRange {
  if (period === "all") return {};

  const now = new Date();
  const to = now.toISOString();

  if (period === "this-year") {
    const from = new Date(now.getFullYear(), 0, 1);

    return {
      completedAtFrom: from.toISOString(),
      completedAtTo: to,
    };
  }

  const daysMap: Record<string, number> = {
    "1mo": 30,
    "3mo": 90,
    "6mo": 180,
  };

  const days = daysMap[period] ?? 30;

  const from = new Date(now);

  from.setDate(from.getDate() - days);

  return {
    completedAtFrom: from.toISOString(),
    completedAtTo: to,
  };
}

interface PerformanceTabProps {
  projects?: Array<{ id: number | string; name: string }>;
  userId?: number | string;
}

export function PerformanceTab({
  projects: _projects,
  userId,
}: PerformanceTabProps) {
  const numericUserId = Number(userId ?? 0);

  const [timePeriod, setTimePeriod] = useState<TimePeriod>("all");
  const dateRange = useMemo(() => getDateRange(timePeriod), [timePeriod]);

  const {
    debouncedQuery,
    inputValue,
    itemsPerPage,
    page,
    setInputValue,
    setItemsPerPage,
    setPage,
    setSortDescriptor,
    sortDescriptor,
  } = usePaginationWithDynamicPageSize();

  const [selectedRating, setSelectedRating] =
    useState<null | RequestRatingEnum>(null);

  const handleRatingClick = (rating: RequestRatingEnum) => {
    setSelectedRating((prev) => (prev === rating ? null : rating));
    setPage(1);
  };

  const handleTimePeriodChange = (period: TimePeriod) => {
    setTimePeriod(period);
    setPage(1);
  };

  const direction: "asc" | "desc" =
    sortDescriptor.direction === "descending" ? "desc" : "asc";

  const designerIdParam = Number.isFinite(numericUserId)
    ? numericUserId
    : undefined;

  const { data: response, isFetching } = useGetProjectView({
    completedAtFrom: dateRange.completedAtFrom,
    completedAtTo: dateRange.completedAtTo,
    completedOnly: true,
    designerId: designerIdParam,
    direction,
    itemsPerPage,
    page,
    projectId: 0,
    rating: selectedRating,
    search: debouncedQuery,
    sortBy: sortDescriptor?.column as string,
  });

  const { data } = useGetDesignerPerformanceSummary({
    completedAtFrom: dateRange.completedAtFrom,
    completedAtTo: dateRange.completedAtTo,
    designerId: designerIdParam ?? 0,
  });

  const ratings = percentages(data?.ratings);

  if (!designerIdParam) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-text-tertiary">No designer selected</p>
      </div>
    );
  }

  return (
    <div>
      <section>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full bg-bg-primary py-3 pt-0">
          <SearchField
            aria-label="Search requests"
            className="w-full sm:flex-1"
          >
            <SearchInput
              onChange={(e) => setInputValue(e.target.value)}
              onClear={() => setInputValue("")}
              placeholder="Search..."
              value={inputValue}
            />
          </SearchField>
          <div className="w-full sm:w-auto overflow-x-auto">
            <SegmentedTabs
              onTabChange={handleTimePeriodChange}
              selectedTab={timePeriod}
              tabs={TIME_PERIOD_TABS}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 my-8">
          {/* Performance Rating Card */}
          <div className="border border-border-primary rounded-md py-5 px-5 h-48">
            <div className="flex">
              <TrendingUpIcon />
              <p className="flex text-color-primary text-base ml-3">
                Performance Rating
              </p>
            </div>
            <p className="text-text-secondary">Overall satisfaction score</p>
            <DonutChart ratings={data?.ratings} />
          </div>

          <RatingCard
            count={data?.ratings.good ?? 0}
            label={DESIGNER_RATING_LABELS[RequestRatingEnum.GOOD]}
            onSelect={handleRatingClick}
            percentage={ratings?.good ?? 0}
            rating={RequestRatingEnum.GOOD}
            selectedRating={selectedRating}
          />

          <RatingCard
            count={data?.ratings.neutral ?? 0}
            label={DESIGNER_RATING_LABELS[RequestRatingEnum.NEUTRAL]}
            onSelect={handleRatingClick}
            percentage={ratings?.neutral ?? 0}
            rating={RequestRatingEnum.NEUTRAL}
            selectedRating={selectedRating}
          />

          <RatingCard
            count={data?.ratings.poor ?? 0}
            label={DESIGNER_RATING_LABELS[RequestRatingEnum.POOR]}
            onSelect={handleRatingClick}
            percentage={ratings?.poor ?? 0}
            rating={RequestRatingEnum.POOR}
            selectedRating={selectedRating}
          />
        </div>

        <PerformanceRequestsTable
          isFetching={isFetching}
          pagination={{
            metadata: response?.metadata ?? {
              currentPage: page,
              itemsPerPage,
              totalItems: 0,
              totalPages: 0,
            },
            onItemsPerPageChange: setItemsPerPage,
            onPageChange: setPage,
            pageSizeOptions: getPageSizeOptions(
              response?.metadata.totalItems ?? 0,
            ),
            showPageSizeSelector: true,
          }}
          requests={response?.requests ?? []}
          setSortDescriptor={setSortDescriptor}
          sortDescriptor={sortDescriptor}
        />
      </section>
    </div>
  );
}
